import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import { auth } from "../firebase";

const AUTH_ERROR_MESSAGES = {
  "auth/email-already-in-use":  "An account already exists with this email.",
  "auth/weak-password":         "Password should be at least 6 characters.",
  "auth/invalid-email":         "Please enter a valid email address.",
  "auth/too-many-requests":     "Too many attempts. Please try again later.",
  "auth/operation-not-allowed": "Sign-in is currently unavailable. Please try again later.",
  "auth/user-not-found":        "No account found with this email.",
  "auth/wrong-password":        "Incorrect password.",
  "auth/invalid-credential":    "Incorrect email or password.",
};

const provider = new GoogleAuthProvider();

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Signup step tracker — account creation, then profile onboarding.
  const signupSteps = [
    { label: "Account", status: "active" },
    { label: "Profile", status: "upcoming" },
  ];
  const activeStepNumber = signupSteps.findIndex((s) => s.status === "active") + 1;
  const totalSteps = signupSteps.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "signup") {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      const msg = AUTH_ERROR_MESSAGES[err.code] ||
        err.message.replace("Firebase: ", "").replace(/\(auth\/[^)]*\)\.?/g, "").trim() ||
        "Something went wrong. Please try again.";
      setError(msg);
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    setError("");
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      if (err.code === "auth/popup-blocked" || err.code === "auth/popup-closed-by-user") {
        try {
          const { signInWithRedirect } = await import("firebase/auth");
          await signInWithRedirect(auth, provider);
        } catch {
          setError("Google sign in failed. Please try again.");
        }
      } else {
        setError(AUTH_ERROR_MESSAGES[err.code] || err.message || "Google sign in failed. Please try again.");
      }
    }
  };

  const SunMark = ({ size = 32 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="8" fill="#F5A623"/>
      {[0,45,90,135,180,225,270,315].map((deg, i) => (
        <line key={i} x1="16" y1="4" x2="16" y2="1.5"
          stroke="#F5A623" strokeWidth="2" strokeLinecap="round"
          transform={`rotate(${deg} 16 16)`}/>
      ))}
    </svg>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .auth-root {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: 'DM Sans', sans-serif;
          background: #FAF6F0;
        }

        /* ── LEFT PANEL ── */
        .auth-left {
          background: #1E2D4A;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 48px;
          position: relative;
          overflow: hidden;
        }
        .auth-left::before {
          content: '';
          position: absolute;
          top: -100px; right: -100px;
          width: 380px; height: 380px;
          background: radial-gradient(circle, rgba(245,166,35,0.15) 0%, transparent 70%);
          pointer-events: none;
        }
        .auth-left::after {
          content: '';
          position: absolute;
          bottom: -80px; left: -60px;
          width: 280px; height: 280px;
          background: radial-gradient(circle, rgba(107,181,214,0.12) 0%, transparent 70%);
          pointer-events: none;
        }

        .auth-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          position: relative;
          z-index: 1;
        }
        .auth-logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: white;
          letter-spacing: -0.02em;
        }

        .auth-hero { position: relative; z-index: 1; }
        .auth-headline {
          font-family: 'Playfair Display', serif;
          font-size: 42px;
          font-weight: 600;
          color: white;
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
        }
        .auth-headline em { font-style: italic; color: #F5A623; }
        .auth-subline {
          font-size: 15px;
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          line-height: 1.65;
          max-width: 340px;
        }

        .auth-features { display: flex; flex-direction: column; gap: 12px; position: relative; z-index: 1; }
        .auth-feature { display: flex; align-items: center; gap: 12px; }
        .auth-feature-icon {
          width: 32px; height: 32px;
          border-radius: 8px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .auth-feature-dot { width: 10px; height: 10px; border-radius: 50%; }
        .auth-feature-text { font-size: 13px; color: rgba(255,255,255,0.65); font-weight: 300; }
        .auth-feature-text strong { color: white; font-weight: 500; }

        /* ── RIGHT PANEL ── */
        .auth-right {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px;
        }
        .auth-form-wrap {
          width: 100%;
          max-width: 380px;
          animation: fadeUp 0.45s ease forwards;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .auth-form-title {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 600;
          color: #1A1714;
          letter-spacing: -0.02em;
          margin-bottom: 6px;
        }
        .auth-form-sub {
          font-size: 14px;
          color: rgba(26,23,20,0.5);
          margin-bottom: 30px;
          font-weight: 300;
        }

        .auth-google-btn {
          width: 100%;
          padding: 13px;
          border: 1.5px solid rgba(26,23,20,0.13);
          border-radius: 12px;
          background: white;
          display: flex; align-items: center; justify-content: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 500;
          color: #1A1714;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          margin-bottom: 20px;
        }
        .auth-google-btn:hover {
          border-color: #F5A623;
          box-shadow: 0 2px 12px rgba(245,166,35,0.12);
        }
        .google-icon { width: 18px; height: 18px; }

        .auth-divider { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
        .auth-divider-line { flex: 1; height: 1px; background: rgba(26,23,20,0.09); }
        .auth-divider-text { font-size: 12px; color: rgba(26,23,20,0.35); }

        .auth-field { margin-bottom: 14px; }
        .auth-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          margin-bottom: 6px;
          letter-spacing: 0.02em;
        }
        .auth-input {
          width: 100%;
          padding: 13px 16px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 12px;
          background: white;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #1A1714;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .auth-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 3px rgba(245,166,35,0.1);
        }
        .auth-input::placeholder { color: rgba(26,23,20,0.28); }
        .auth-pw-wrap { position: relative; }
        .auth-pw-wrap .auth-input { padding-right: 44px; }
        .auth-pw-toggle {
          position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(26,23,20,0.35); padding: 4px;
          display: flex; align-items: center; line-height: 1;
        }
        .auth-pw-toggle:hover { color: rgba(26,23,20,0.65); }

        .auth-error {
          background: #fef0ed;
          border: 1px solid rgba(201,98,47,0.2);
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          color: #c9622f;
          margin-bottom: 14px;
        }

        .auth-submit {
          width: 100%;
          padding: 14px;
          background: #1E2D4A;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          margin-top: 6px;
        }
        .auth-submit:hover:not(:disabled) { background: #2C3E5E; transform: translateY(-1px); }
        .auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        .auth-switch {
          text-align: center;
          margin-top: 20px;
          font-size: 13px;
          color: rgba(26,23,20,0.5);
        }
        .auth-switch button {
          background: none; border: none;
          color: #C47A0A; font-weight: 500; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 13px;
          padding: 0; margin-left: 4px;
        }
        .auth-switch button:hover { text-decoration: underline; }
        .auth-terms {
          text-align: center;
          margin-top: 16px;
          font-size: 11px;
          color: rgba(26,23,20,0.3);
          line-height: 1.5;
        }

        /* ── INVITE CODE OVERLAY ── */
        .invite-overlay {
          position: fixed;
          inset: 0;
          background: #FAF6F0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 24px;
          animation: fadeUp 0.4s ease forwards;
        }
        .invite-glow {
          position: fixed;
          top: 0; left: 0; right: 0;
          height: 320px;
          background: radial-gradient(ellipse 80% 100% at 50% -10%, rgba(245,166,35,0.18) 0%, transparent 70%);
          pointer-events: none;
        }
        .invite-box {
          width: 100%;
          max-width: 440px;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .invite-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 36px;
        }
        .invite-logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 600;
          color: #1E2D4A;
          letter-spacing: -0.02em;
        }
        .invite-sun {
          font-size: 52px;
          display: block;
          margin-bottom: 24px;
          filter: drop-shadow(0 4px 16px rgba(245,166,35,0.35));
          line-height: 1;
        }
        .invite-headline {
          font-family: 'Playfair Display', serif;
          font-size: 30px;
          font-weight: 600;
          color: #1E2D4A;
          letter-spacing: -0.02em;
          line-height: 1.2;
          margin-bottom: 12px;
        }
        .invite-sub {
          font-size: 15px;
          color: rgba(26,23,20,0.52);
          font-weight: 300;
          line-height: 1.65;
          margin-bottom: 32px;
          max-width: 360px;
          margin-left: auto;
          margin-right: auto;
        }
        .invite-input {
          width: 100%;
          padding: 16px 20px;
          border: 2px solid rgba(26,23,20,0.12);
          border-radius: 14px;
          background: white;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          color: #1E2D4A;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          text-align: center;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          margin-bottom: 10px;
          box-shadow: 0 2px 12px rgba(26,23,20,0.04);
        }
        .invite-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 4px rgba(245,166,35,0.12);
        }
        .invite-input::placeholder {
          color: rgba(26,23,20,0.28);
          text-transform: none;
          letter-spacing: 0;
          font-size: 14px;
        }
        .invite-error {
          color: #c9622f;
          font-size: 13px;
          margin-bottom: 10px;
          background: #fef0ed;
          border: 1px solid rgba(201,98,47,0.2);
          border-radius: 10px;
          padding: 10px 14px;
          text-align: left;
        }
        .invite-btn {
          width: 100%;
          padding: 16px;
          background: #F5A623;
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          margin-bottom: 22px;
          box-shadow: 0 4px 20px rgba(245,166,35,0.3);
          letter-spacing: 0.01em;
        }
        .invite-btn:hover:not(:disabled) {
          background: #C47A0A;
          transform: translateY(-1px);
          box-shadow: 0 6px 24px rgba(245,166,35,0.38);
        }
        .invite-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .invite-waitlist {
          font-size: 13px;
          color: rgba(26,23,20,0.45);
          margin-bottom: 24px;
        }
        .invite-waitlist a {
          color: #C47A0A;
          font-weight: 500;
          text-decoration: none;
        }
        .invite-waitlist a:hover { text-decoration: underline; }
        .invite-back {
          background: none;
          border: none;
          font-size: 13px;
          color: rgba(26,23,20,0.38);
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          padding: 0;
        }
        .invite-back:hover { color: rgba(26,23,20,0.6); }

        @media (max-width: 768px) {
          .auth-root { grid-template-columns: 1fr; }
          .auth-left { display: none; }
          .auth-right { padding: 32px 24px; }
          .invite-headline { font-size: 26px; }
        }

        /* ── NEW SIGNUP TWO-PANEL (su-) — independent of .auth-root ── */
        .su-root {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: 'DM Sans', sans-serif;
          background: var(--sol-cream);
        }

        /* LEFT PANEL */
        .su-left {
          background: var(--sol-navy);
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          padding: 48px;
          position: relative;
          overflow: hidden;
        }
        .su-sunrise {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 60%;
          background: radial-gradient(ellipse 100% 75% at 50% 100%, rgba(245,166,35,0.28) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .su-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .su-logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: var(--sol-cream);
          letter-spacing: -0.02em;
        }
        .su-hero { position: relative; z-index: 1; margin-top: auto; margin-bottom: auto; }
        .su-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(245,166,35,0.15);
          border: 1px solid rgba(245,166,35,0.25);
          border-radius: 100px;
          padding: 5px 12px;
          font-size: 11px;
          font-weight: 500;
          color: var(--sol-amber);
          letter-spacing: 0.07em;
          text-transform: uppercase;
          margin-bottom: 20px;
        }
        .su-badge svg { width: 13px; height: 13px; }
        .su-headline {
          font-family: 'Playfair Display', serif;
          font-size: 42px;
          font-weight: 600;
          color: var(--sol-cream);
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
        }
        .su-headline em { font-style: italic; color: var(--sol-amber); }
        .su-body {
          font-size: 15px;
          font-weight: 300;
          color: rgba(255,255,255,0.7);
          line-height: 1.65;
          max-width: 340px;
        }

        /* RIGHT PANEL */
        .su-right {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px;
        }
        .su-form-wrap {
          width: 100%;
          max-width: 400px;
          animation: fadeUp 0.45s ease forwards;
        }

        /* STEP TRACKER */
        .su-tracker { display: flex; margin-bottom: 34px; }
        .su-step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }
        .su-step-dot {
          width: 30px; height: 30px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 500;
          position: relative; z-index: 1;
        }
        .su-step:not(:first-child)::before {
          content: '';
          position: absolute;
          top: 15px; left: -50%; right: 50%;
          height: 2px;
          background: rgba(26,23,20,0.12);
          z-index: 0;
        }
        .su-step-active::before,
        .su-step-complete::before { background: var(--sol-amber); }
        .su-step-complete .su-step-dot { background: var(--sol-amber); color: #fff; }
        .su-step-active .su-step-dot { background: var(--sol-navy); color: #fff; }
        .su-step-upcoming .su-step-dot {
          background: #fff;
          color: rgba(26,23,20,0.4);
          border: 1.5px solid rgba(26,23,20,0.15);
        }
        .su-step-label { font-size: 12px; margin-top: 8px; }
        .su-step-complete .su-step-label { color: rgba(26,23,20,0.5); }
        .su-step-active .su-step-label { color: var(--sol-navy); font-weight: 500; }
        .su-step-upcoming .su-step-label { color: rgba(26,23,20,0.38); }

        .su-title {
          font-family: 'Playfair Display', serif;
          font-size: 28px;
          font-weight: 600;
          color: var(--sol-navy);
          letter-spacing: -0.02em;
          margin-bottom: 6px;
        }
        .su-subtitle {
          font-size: 14px;
          color: rgba(30,45,74,0.55);
          margin-bottom: 20px;
          font-weight: 300;
        }

        .su-google {
          width: 100%;
          padding: 13px;
          border: 1.5px solid rgba(26,23,20,0.13);
          border-radius: 13px;
          background: #fff;
          display: flex; align-items: center; justify-content: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 500;
          color: var(--sol-ink);
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          margin-bottom: 20px;
        }
        .su-google:hover {
          border-color: var(--sol-amber);
          box-shadow: 0 2px 12px rgba(245,166,35,0.12);
        }
        .su-google svg { width: 18px; height: 18px; }

        .su-divider { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
        .su-divider-line { flex: 1; height: 1px; background: rgba(26,23,20,0.09); }
        .su-divider-text { font-size: 12px; color: rgba(26,23,20,0.35); }

        .su-field { margin-bottom: 14px; }
        .su-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          margin-bottom: 6px;
          letter-spacing: 0.02em;
        }
        .su-input {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid rgba(26,23,20,0.12);
          border-radius: 13px;
          background: #fff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: var(--sol-ink);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .su-input:focus {
          border-color: var(--sol-amber);
          box-shadow: 0 0 0 4px rgba(245,166,35,0.12);
        }
        .su-input::placeholder { color: rgba(26,23,20,0.28); }
        .su-pw-wrap { position: relative; }
        .su-pw-wrap .su-input { padding-right: 44px; }
        .su-pw-toggle {
          position: absolute; right: 13px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(26,23,20,0.35); padding: 4px;
          display: flex; align-items: center; line-height: 1;
        }
        .su-pw-toggle:hover { color: rgba(26,23,20,0.65); }

        .su-error {
          background: #fef0ed;
          border: 1px solid rgba(201,98,47,0.2);
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          color: #c9622f;
          margin-bottom: 14px;
        }

        .su-submit {
          width: 100%;
          padding: 14px;
          background: var(--sol-amber);
          color: #fff;
          border: none;
          border-radius: 13px;
          font-size: 15px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          margin-top: 6px;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 4px 20px rgba(245,166,35,0.3);
        }
        .su-submit:hover:not(:disabled) {
          background: var(--sol-amber-dk);
          transform: translateY(-1px);
          box-shadow: 0 6px 24px rgba(245,166,35,0.38);
        }
        .su-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .su-submit svg { width: 16px; height: 16px; }

        .su-footer {
          text-align: center;
          margin-top: 22px;
          font-size: 13px;
          color: rgba(26,23,20,0.5);
        }
        .su-footer button {
          background: none; border: none;
          color: var(--sol-amber-dk);
          font-weight: 500; cursor: pointer;
          font-family: 'DM Sans', sans-serif; font-size: 13px;
          padding: 0; margin-left: 4px;
        }
        .su-footer button:hover { text-decoration: underline; }

        @media (max-width: 768px) {
          .su-root { grid-template-columns: 1fr; }
          .su-left { display: none; }
          .su-right { padding: 32px 24px; }
        }
      `}</style>

      {/* ── SIGNUP TWO-PANEL ── */}
      {mode === "signup" && (
        <div className="su-root">

          {/* ── LEFT PANEL ── */}
          <div className="su-left">
            <div className="su-logo">
              <SunMark size={32} />
              <span className="su-logo-text">Solis</span>
            </div>

            <div className="su-hero">
              <div className="su-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 22h20"/>
                  <path d="M6.36 17.4 4 17l-2-4 1.1-.55a2 2 0 0 1 1.8 0l.17.1a2 2 0 0 0 1.8 0L8 12 5 6l.9-.45a2 2 0 0 1 2.09.2l4.02 3a2 2 0 0 0 2.1.2l4.19-2.06a2.41 2.41 0 0 1 1.73-.17L21 7a1.4 1.4 0 0 1 .87 1.99l-.38.76c-.23.46-.6.84-1.07 1.08L7.58 17.2a2 2 0 0 1-1.22.18Z"/>
                </svg>
                Now boarding
              </div>
              <h1 className="su-headline">Welcome to <em>Solis</em>.</h1>
              <p className="su-body">
                Glad you're here. Set up your account and we'll take it from there.
              </p>
            </div>

            <div className="su-sunrise" />
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="su-right">
            <div className="su-form-wrap">

              {/* Step tracker — derived from signupSteps */}
              <div className="su-tracker">
                {signupSteps.map((s, i) => (
                  <div className={`su-step su-step-${s.status}`} key={s.label}>
                    <div className="su-step-dot">{s.status === "complete" ? "✓" : i + 1}</div>
                    <span className="su-step-label">{s.label}</span>
                  </div>
                ))}
              </div>

              <h2 className="su-title">Create your account</h2>
              <p className="su-subtitle">Step {activeStepNumber} of {totalSteps} — almost there.</p>

              <button className="su-google" onClick={handleGoogle}>
                <svg viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div className="su-divider">
                <div className="su-divider-line"></div>
                <span className="su-divider-text">or</span>
                <div className="su-divider-line"></div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="su-field">
                  <label className="su-label">Email address</label>
                  <input className="su-input" type="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="su-field">
                  <label className="su-label">Password</label>
                  <div className="su-pw-wrap">
                    <input className="su-input" type={showPassword ? "text" : "password"}
                      placeholder="At least 6 characters"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="su-pw-toggle" onClick={() => setShowPassword(s => !s)}>
                      {showPassword ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {error && <div className="su-error">{error}</div>}
                <button className="su-submit" type="submit" disabled={loading}>
                  {loading ? "Please wait..." : "Create account"}
                  {!loading && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  )}
                </button>
              </form>

              <div className="su-footer">
                Already have an account?
                <button onClick={() => { setMode("login"); setError(""); }}>
                  Sign in
                </button>
              </div>

            </div>
          </div>

        </div>
      )}

      {mode === "login" && (
      <div className="auth-root">

        {/* ── LEFT PANEL ── */}
        <div className="auth-left">
          <div className="auth-logo">
            <SunMark size={32} />
            <span className="auth-logo-text">Solis</span>
          </div>

          <div className="auth-hero">
            <h1 className="auth-headline">
              Discover more.<br /><em>Stress less.</em>
            </h1>
            <p className="auth-subline">
              The AI-powered group travel app that plans around everyone — so you can focus on the memories.
            </p>
          </div>

          <div className="auth-features">
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <div className="auth-feature-dot" style={{background:"#F5A623"}}></div>
              </div>
              <p className="auth-feature-text"><strong>AI itineraries</strong> built around your whole group</p>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <div className="auth-feature-dot" style={{background:"#6BB5D6"}}></div>
              </div>
              <p className="auth-feature-text"><strong>Local tips</strong> from people who actually live there</p>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <div className="auth-feature-dot" style={{background:"#1D9E75"}}></div>
              </div>
              <p className="auth-feature-text"><strong>Group expenses</strong> split fairly, settled easily</p>
            </div>
            <div className="auth-feature">
              <div className="auth-feature-icon">
                <div className="auth-feature-dot" style={{background:"#F5A623"}}></div>
              </div>
              <p className="auth-feature-text"><strong>Travel journal</strong> your whole group builds together</p>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="auth-right">
          <div className="auth-form-wrap">
            <h2 className="auth-form-title">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p className="auth-form-sub">
              {mode === "login"
                ? "Sign in to continue planning your next adventure."
                : "Join Solis free. No credit card needed."}
            </p>

            <button className="auth-google-btn" onClick={handleGoogle}>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="auth-divider">
              <div className="auth-divider-line"></div>
              <span className="auth-divider-text">or</span>
              <div className="auth-divider-line"></div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="auth-field">
                <label className="auth-label">Email address</label>
                <input className="auth-input" type="email" placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <div className="auth-pw-wrap">
                  <input className="auth-input" type={showPassword ? "text" : "password"}
                    placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                    value={password} onChange={(e) => setPassword(e.target.value)} required />
                  <button type="button" className="auth-pw-toggle" onClick={() => setShowPassword(s => !s)}>
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {error && <div className="auth-error">{error}</div>}
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? "Please wait..."
                  : mode === "login" ? "Sign in to Solis"
                  : "Create my account"}
              </button>
            </form>

            <div className="auth-switch">
              {mode === "login" ? "Don't have an account?" : "Already have an account?"}
              <button onClick={() => {
                const next = mode === "login" ? "signup" : "login";
                setMode(next);
                setError("");
              }}>
                {mode === "login" ? "Sign up free" : "Sign in"}
              </button>
            </div>

            <p className="auth-terms">
              By continuing you agree to Solis's Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>

      </div>
      )}
    </>
  );
}
